<script setup>
import { html } from '@codemirror/lang-html';
import { tags } from '@lezer/highlight';
import { basicSetup } from 'codemirror';
import { ErrorMessage, Field } from 'vee-validate';
import { computed } from 'vue';
import { Codemirror } from 'vue-codemirror';

const {
    fieldId,
    fieldName,
    required = false,
    label,
    language = 'html',
    size = 'medium',
    legend = undefined,
} = defineProps({
    fieldId: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        required: false,
    },
    label: {
        type: String,
        required: true,
    },
    language: {
        type: String,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    legend: {
        type: String,
        required: false,
    },
});

const model = defineModel();

const extensions = computed(() => {
    if (language === 'mjml') {
        return [
            html({
                allowCustomTags: true,
                matchClosingTags: false,
                selfClosingTags: [
                    'mjml',
                    'mj-body',
                    'mj-section',
                    'mj-column',
                    'mj-text',
                    'mj-image',
                ],
                highlight: {
                    tagName: tags.tagName,
                    attributeName: tags.attributeName,
                    attributeValue: tags.string,
                },
            }),
        ];
    }

    return [basicSetup, html()];
});
</script>

<template>
    <div class="mb-4 flex flex-col">
        <label :for="fieldName" class="mb-2">
            {{ label }}
            <sup v-if="required" class="text-red-500">*</sup>
        </label>

        <Field v-model="model" :id="fieldId" :name="fieldName">
            <Codemirror
                v-model="model"
                :style="{
                    height:
                        size === 'small'
                            ? '20rem'
                            : size === 'medium'
                              ? '40rem'
                              : '60rem',
                }"
                :indent-with-tab="true"
                :tab-size="4"
                :extensions="extensions"
            />
        </Field>

        <em
            v-if="legend"
            class="mt-1 text-xs text-gray-400 [&>a]:underline"
            v-html="legend"
        ></em>

        <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
    </div>
</template>
